import axios from 'axios'
import { Auth } from 'aws-amplify'
import { newRequestKey } from './billing'

const client = axios.create({ baseURL: process.env.VUE_APP_ROOT_API, timeout: 30000 })
const pending = new Map()

export function creditsChanged() {
  window.dispatchEvent(new Event('billing:changed'))
  try { localStorage.setItem('billing-updated', String(Date.now())) } catch (_) { /* Storage may be disabled. */ }
}

export function aiError(error) {
  return (error.response && error.response.data && error.response.data.message)
    || error.message || 'AI is temporarily unavailable. Please try again.'
}

// Keep only an input hash, request key and job ID in session storage, never text or tokens.
// A network retry resumes the original job even if the component or page was reopened.
export async function runAi(operation, payload) {
  const session = await Auth.currentSession()
  const subject = session.getAccessToken().payload.sub
  const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify(payload)))
  const hash = Array.from(new Uint8Array(digest), value => value.toString(16).padStart(2, '0')).join('')
  const storageKey = `ai-job:${subject}:${operation}:${hash}`
  let state = pending.get(storageKey)
  try { state = state || JSON.parse(sessionStorage.getItem(storageKey)) } catch (_) { /* Use the in-memory retry key. */ }
  if (!state) state = { key: newRequestKey() }
  const save = () => {
    pending.set(storageKey, state)
    try { sessionStorage.setItem(storageKey, JSON.stringify(state)) } catch (_) { /* Keep in-memory state. */ }
  }
  const clear = () => {
    pending.delete(storageKey)
    try { sessionStorage.removeItem(storageKey) } catch (_) { /* Storage may be disabled. */ }
  }
  const request = async (method, url, data) => {
    const current = await Auth.currentSession()

    return client.request({
      method,
      url,
      data,
      headers: {
        Authorization: current.getAccessToken().getJwtToken(), 'Idempotency-Key': state.key,
      },
    })
  }
  save()
  try {
    let result
    if (!state.jobId) {
      result = (await request('post', operation === 'text' ? '/aiTextAction' : '/restoreImage', payload)).data
      state.jobId = result.jobId
      save()
      creditsChanged()
    }
    for (let attempt = 0; attempt < 180; attempt += 1) {
      // Each poll depends on the previous result; concurrent polling would duplicate work.
      // eslint-disable-next-line no-await-in-loop
      if (!result) result = (await request('get', `/ai/jobs/${state.jobId}`)).data
      if (result.status === 'succeeded') {
        clear(); creditsChanged()

        return result
      }
      if (result.status === 'failed') { clear(); creditsChanged(); throw new Error(result.message) }
      if (result.status === 'review') throw new Error(`${result.message} Request: ${result.jobId}`)
      // eslint-disable-next-line no-await-in-loop
      await new Promise(resolve => setTimeout(resolve, 2000))
      result = null
    }
    throw new Error('Your AI request is still running. Try the same action again to resume it without another charge.')
  } catch (error) {
    if (error.response && [400, 401, 402, 403, 404, 410, 422, 429].includes(error.response.status)) clear()
    if (error.response && error.response.status === 409 && !state.jobId) clear()
    if (!error.response && !error.message) throw new Error('Unable to reach AI. Retry the same action to resume your request.')
    throw error
  }
}
